import { useQuery } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { GetEventOrdersDto } from '@/types/dtos/orders/response/get-event-orders.dto.ts';
import { ServerError } from '@/types/errors/server.error';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { EVENT_CATALOG_ORDERS_FEATURE } from '@/constants/feature-flags';

export const GET_EVENT_ORDERS_QUERY_KEY = 'get-event-orders';

export const useGetEventOrdersQuery = (eventId?: string) => {
  const { getToken } = useAuth();
  const { hasAccess } = useFeatureAccess(EVENT_CATALOG_ORDERS_FEATURE);

  const query = useQuery({
    queryKey: [GET_EVENT_ORDERS_QUERY_KEY, eventId],
    queryFn: async () => {
      const token = await getToken();
      try {
        return await getEventOrders(eventId!, token ?? '');
      } catch (error) {
        // Handle 403 Forbidden errors gracefully - user doesn't have permission to read orders
        if (error instanceof ServerError && error.statusCode === 403) {
          // Return empty array instead of throwing (GetEventOrdersDto is OrderDto[])
          return [] as GetEventOrdersDto;
        }
        throw error;
      }
    },
    enabled: !!eventId && hasAccess,
    retry: false, // Don't retry on 403/404 errors
  });

  // Mask cached order data the moment access is revoked, even before the query key is evicted.
  if (!hasAccess) {
    return { ...query, data: undefined } as typeof query;
  }
  return query;
};

const getEventOrders = async (eventId: string, token: string): Promise<GetEventOrdersDto> => {
  const url = `${import.meta.env.VITE_API_URL}/orders/event/${eventId}`;
  return sendJSONRequest({ url, method: METHOD.GET, token });
};
